// UI strings for the Halloween script (Axel's decisions of 2026-10-09; together/halloween/): the
// sentence inbox (Lauseideat), the script (Käsikirjoitus), dropped lines (Hylätyt), Lavalla and the
// Esitys card. Finnish (default) and English side by side, the same shape. Style: docs/FINNISH-STYLE.md.
import { defineArea } from '../area';

export const esitysFi = {
  /** The rows on the Halloween page. */
  rows: {
    script: 'Käsikirjoitus',
    scriptCount: (n: number) => (n === 0 ? 'Ei vielä repliikkejä' : n === 1 ? '1 repliikki' : `${n} repliikkiä`),
    ideas: 'Lauseideat',
    ideasNone: 'Kirjoita lause, jonka haluat osata sanoa juhlissa',
    ideasCount: (waiting: number, toApprove: number) =>
      [waiting ? `${waiting} odottaa käännöstä` : '', toApprove ? `${toApprove} odottaa hyväksyntää` : ''].filter(Boolean).join(' · ') ||
      'Kirjoita lause, jonka haluat osata sanoa juhlissa',
    addIdea: 'Kirjoita lauseidea',
    waiting: (n: number) => (n === 1 ? 'Tänään harjoiteltavana 1 Halloween-repliikki' : `Tänään harjoiteltavana ${n} Halloween-repliikkiä`),
  },
  ideas: {
    title: 'Lauseideat',
    field: 'Uusi lause suomeksi',
    placeholder: 'esim. Kiitos kutsusta!',
    add: 'Lisää',
    added: 'Lause lisätty. Se käännetään valyriaksi seuraavassa tarkistussessiossa.',
    toApprove: 'Odottaa hyväksyntää',
    waiting: 'Odottaa käännöstä',
    inScript: 'Käsikirjoituksessa',
    wroteYou: (date: string) => `Kirjoitit ${date}.`,
    wrote: (name: string, date: string) => `${name} kirjoitti ${date}.`,
    translated: (date: string) => `Käännetty tarkistussessiossa ${date}.`,
    approvedBy: (name: string) => `${name} hyväksyi.`,
    approvedByYou: 'Hyväksyit.',
    edit: 'Muokkaa',
    save: 'Tallenna',
    cancel: 'Peru',
    approve: 'Hyväksy',
    unapprove: 'Peru hyväksyntä',
    approved: 'Lause hyväksytty. Se on nyt käsikirjoituksessa.',
    english: 'Englanniksi',
    note: 'Huomautus tarkistussessiosta',
  },
  script: {
    title: 'Käsikirjoitus',
    stage: 'Lavalle',
    empty: 'Käsikirjoituksessa ei ole vielä repliikkejä.',
    emptyAction: 'Kirjoita lauseidea',
    speaker: 'Kuka sanoo',
    both: 'Molemmat',
    up: 'Siirrä ylemmäs',
    down: 'Siirrä alemmas',
    ideaSource: 'Lauseideanne, käännetty tarkistussessiossa',
    ownSource: 'Teidän oma versionne',
    noCard: 'Repliikiltä puuttuu suomennos, joten siitä ei tehdä korttia.',
  },
  drop: {
    drop: 'Hylkää',
    dropped: 'Lause hylätty.',
    droppedLine: 'Repliikki hylätty.',
    undo: 'Kumoa',
    undoLabel: 'Kumoa hylkäys',
    restore: 'Palauta',
    restored: 'Palautettu.',
    title: 'Hylätyt',
    link: (n: number) => `Hylätyt (${n})`,
    droppedBy: (name: string, date: string) => `${name} hylkäsi ${date}.`,
    droppedByYou: (date: string) => `Hylkäsit ${date}.`,
    none: 'Hylättyjä ei ole.',
  },
  stage: {
    title: 'Lavalla',
    close: 'Sulje',
    as: 'Kenen repliikit',
    onlyMine: 'Vain omat',
    showAll: 'Näytä kaikki',
    hideAll: 'Piilota omat',
    tap: 'Napauta, niin näet repliikin valyriaksi',
    hide: 'Piilota',
    cue: 'Edellinen repliikki',
    both: 'Molemmat',
    empty: 'Käsikirjoitus on vielä tyhjä.',
  },
  /** The week's line under Yhdessä (WeekHalloween.tsx). */
  week: {
    title: 'Halloween',
    newLine: 'uusi repliikki käsikirjoitukseen –',
    waiting: (n: number) => (n === 1 ? 'tänään harjoiteltavana 1 repliikki' : `tänään harjoiteltavana ${n} repliikkiä`),
    rehearse: 'käykää käsikirjoitus läpi ääneen',
    stage: 'avatkaa Lavalla-näkymä',
    addIdea: 'kirjoita lauseidea',
  },
  /** <HalloweenScript /> in a lesson (LessonScript.tsx). */
  lesson: {
    ideas: 'Kirjoita lauseidea',
    script: (n: number) => (n === 0 ? 'Käsikirjoitus' : n === 1 ? 'Käsikirjoitus · 1 repliikki' : `Käsikirjoitus · ${n} repliikkiä`),
  },
  card: {
    kind: 'Halloween-repliikki',
    say: 'Sano ääneen valyriaksi',
    initials: 'Sanojen alkukirjaimet',
    ourTake: 'Teidän äänityksenne',
  },
};

export type EsitysMessages = typeof esitysFi;

export const esitysEn: EsitysMessages = {
  rows: {
    script: 'Script',
    scriptCount: (n) => (n === 0 ? 'No lines yet' : n === 1 ? '1 line' : `${n} lines`),
    ideas: 'Sentence ideas',
    ideasNone: 'Write a sentence you want to be able to say at the feast',
    ideasCount: (waiting, toApprove) =>
      [waiting ? `${waiting} waiting for a translation` : '', toApprove ? `${toApprove} waiting for approval` : ''].filter(Boolean).join(' · ') ||
      'Write a sentence you want to be able to say at the feast',
    addIdea: 'Write a sentence idea',
    waiting: (n) => (n === 1 ? '1 Halloween line to practise today' : `${n} Halloween lines to practise today`),
  },
  ideas: {
    title: 'Sentence ideas',
    field: 'New sentence in Finnish',
    placeholder: 'e.g. Kiitos kutsusta!',
    add: 'Add',
    added: 'Sentence added. It will be translated into High Valyrian in the next session.',
    toApprove: 'Waiting for approval',
    waiting: 'Waiting for a translation',
    inScript: 'In the script',
    wroteYou: (date) => `You wrote it on ${date}.`,
    wrote: (name, date) => `${name} wrote it on ${date}.`,
    translated: (date) => `Translated in a session on ${date}.`,
    approvedBy: (name) => `${name} approved it.`,
    approvedByYou: 'You approved it.',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    approve: 'Approve',
    unapprove: 'Take back approval',
    approved: 'Sentence approved. It is in the script now.',
    english: 'In English',
    note: 'Note from the session',
  },
  script: {
    title: 'Script',
    stage: 'On stage',
    empty: 'The script has no lines yet.',
    emptyAction: 'Write a sentence idea',
    speaker: 'Who says it',
    both: 'Both',
    up: 'Move up',
    down: 'Move down',
    ideaSource: 'Your sentence idea, translated in a session',
    ownSource: 'Your own version',
    noCard: 'The line has no Finnish, so it has no card.',
  },
  drop: {
    drop: 'Drop',
    dropped: 'Sentence dropped.',
    droppedLine: 'Line dropped.',
    undo: 'Undo',
    undoLabel: 'Undo the drop',
    restore: 'Restore',
    restored: 'Restored.',
    title: 'Dropped',
    link: (n) => `Dropped (${n})`,
    droppedBy: (name, date) => `Dropped by ${name} on ${date}.`,
    droppedByYou: (date) => `You dropped it on ${date}.`,
    none: 'Nothing dropped.',
  },
  stage: {
    title: 'On stage',
    close: 'Close',
    as: 'Whose lines',
    onlyMine: 'Only mine',
    showAll: 'Show all',
    hideAll: 'Hide mine',
    tap: 'Tap to see it in High Valyrian',
    hide: 'Hide',
    cue: 'Previous line',
    both: 'Both',
    empty: 'The script is still empty.',
  },
  week: {
    title: 'Halloween',
    newLine: 'a new line for the script:',
    waiting: (n) => (n === 1 ? '1 line to practise today' : `${n} lines to practise today`),
    rehearse: 'go through the script aloud together',
    stage: 'open the On stage view',
    addIdea: 'write a sentence idea',
  },
  lesson: {
    ideas: 'Write a sentence idea',
    script: (n) => (n === 0 ? 'Script' : n === 1 ? 'Script · 1 line' : `Script · ${n} lines`),
  },
  card: {
    kind: 'Halloween line',
    say: 'Say it aloud in High Valyrian',
    initials: 'First letters of the words',
    ourTake: 'Your recording',
  },
};

// Messages.esitys (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('esitys', esitysFi, esitysEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof esitysFi> {}
}
